import {
  type FacilityType,
  type FacilityWithEquipmentType,
  FacilityGetListRequestSchema,
} from "../schemas/FacilitySchemas.ts";
import type { ReportWithDownloadV2Type } from "../schemas/ReportSchemas.ts";
import {
  type CreateOneRequestType,
  type GetListRequestType,
  type GetListResponseType,
  type GetOneRequestType,
  Resources,
  type UpdateOneRequestType,
  fetchAndCollate,
  getFilterObject,
} from "../util/index.ts";
import type {
  ResourceAdapterType,
  ResourceAdapterToAdapterQueryKeysType,
} from "../util/index.ts";
import { restAPI } from "./api.ts";
import {
  SavedFiltersAdapter,
  type SavedFiltersAdapterType,
} from "./composable/index.ts";

export interface FacilityAdapterType
  extends Pick<
    ResourceAdapterType<FacilityType>,
    | "getOne"
    | "getList"
    | "updateOne"
    | "createOne"
    | "deleteOne"
    | "exportList"
  > {
  savedFilters: SavedFiltersAdapterType<
    typeof FacilityGetListRequestSchema.shape.filters
  >;
}

export const FacilityAdapter: FacilityAdapterType = {
  /**
   * Get Facility API
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/get_facility
   */
  getOne: ({ id, meta }: GetOneRequestType<{ period: string }>) =>
    restAPI.nodeAPI
      .GET<FacilityWithEquipmentType>({
        endpoint: `/facilities/${id}`,
        query: meta?.period ? { period: meta.period } : {},
      })
      .then((resp) => ({
        data: resp,
      })),
  /**
   * Facility Search API
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/searchFacility
   */
  getList: ({
    filters,
    period,
    page,
    pageSize,
    sortBy,
    sortDirection,
    groupBy,
    isExcelFile = false,
  }: GetListRequestType & { period?: string; isExcelFile?: boolean }) => {
    if (isExcelFile) {
      return restAPI.nodeAPI.POST<GetListResponseType<FacilityType>>({
        endpoint: "/facilities/search",
        query: period ? { period } : undefined,
        body: {
          page,
          page_size: pageSize,
          sort_by: sortBy,
          sort_direction: sortDirection,
          filter: getFilterObject(filters),
        },
        headers: {
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        responseType: "arraybuffer",
      });
    } else {
      return fetchAndCollate(
        ({ page, pageSize }) =>
          restAPI.nodeAPI.POST<GetListResponseType<FacilityType>>({
            endpoint: "/facilities/search",
            query: period ? { period } : undefined,
            body: {
              page,
              page_size: pageSize,
              sort_by: sortBy,
              sort_direction: sortDirection,
              group_by: groupBy,
              filter: getFilterObject(filters),
            },
          }),
        page,
        pageSize
      );
    }
  },
  /**
   * Facility Export API
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/exportFacilities
   */
  exportList: ({
    filters,
    period,
    sortBy,
    sortDirection,
  }: GetListRequestType & { period?: string }) => {
    return restAPI.nodeAPI.POST<ReportWithDownloadV2Type>({
      endpoint: "/facilities/export",
      query: period ? { period } : undefined,
      body: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: getFilterObject(filters),
      },
    });
  },
  /**
   * Create Facility API
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/createFacility
   *
   */
  createOne: (params: CreateOneRequestType<FacilityType>) =>
    restAPI.nodeAPI
      .POST<FacilityType>({
        endpoint: "/facilities",
        body: params.data,
      })
      .then((resp) => {
        return { data: resp };
      }),
  /**
   * Update Facility API
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/update_facility
   */
  updateOne: ({ id, data }: UpdateOneRequestType<FacilityType>) =>
    restAPI.nodeAPI
      .PUT<FacilityType>({
        endpoint: `/facilities/${id}`,
        body: data,
      })
      .then((resp) => ({ data: resp })),
  /**
   * Delete Facility API
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/delete_facility
   */
  deleteOne: ({ id }: { id: string }) =>
    restAPI.nodeAPI.DELETE({
      endpoint: `/facilities/${id}`,
    }),

  /**
   * Facility Saved Filter APIs
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/get_facilities_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/post_facility_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/put_facilities_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/facilities/delete_facilities_filter
   */
  savedFilters: SavedFiltersAdapter({ endpoint: "/facilities/filters" }),
};

export const FacilityAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<FacilityAdapterType> =
  {
    getList: (params) => [Resources.FACILITY, "getList", params] as const,
    getOne: (params) => [Resources.FACILITY, "getOne", params.id] as const,
    updateOne: (_) => [Resources.FACILITY] as const,
    createOne: (_) => [Resources.FACILITY] as const,
    deleteOne: (_) => [Resources.FACILITY] as const,
    savedFilters: {
      getList: (params) =>
        [Resources.FACILITY, "filters", "getList", params] as const,
      updateOne: () => [Resources.FACILITY, "filters"] as const,
      createOne: (_) => [Resources.FACILITY, "filters"] as const,
      deleteOne: (_) => [Resources.FACILITY, "filters"] as const,
    },
  };
